/**
 * `fireAndForget` helper for marking intentionally non-awaited Promises so they
 * pass `no-floating-promises`.
 *
 * Wraps a promise with `.catch(captureException)` so rejections route to Sentry
 * with the original `Error` stack preserved (the global `console.error` patch
 * in `instrument.ts` stringifies its args and loses the original Error).
 * Tolerates `null` / `undefined` defensively via optional chaining:
 * TypeScript's structural checking has gaps (Jest manual mocks, the void-return
 * assignability rule) that let a value typed as `Promise` actually be
 * `undefined` at runtime, and we'd rather no-op than crash with a stack
 * pointing at this file instead of the real call site.
 */

import { useEffect, useState } from 'react';
import { captureException } from 'util/sentry';

export function fireAndForget(promise: Promise<unknown>): void {
    // eslint-disable-next-line @repo/psi/prefer-fire-and-forget -- this IS the implementation of fireAndForget
    promise?.catch(captureException);
}

/**
 * Effect that reports a checker's derived enabled boolean up to its parent via
 * a stable setter, firing only when the value changes. For renderless "checker"
 * components that gate a sibling widget's visibility without the parent having
 * to compute it.
 */
export function useCheckerEnabledEffect(setEnabled: (enabled: boolean) => void, enabled: boolean) {
    useEffect(() => {
        setEnabled(enabled);
    }, [setEnabled, enabled]);
}

/**
 * `value`, held back until it has stopped changing for `delayMs`. For a value
 * that drives a request, such as the text in a search box.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timeout = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timeout);
    }, [value, delayMs]);
    return debounced;
}
